/**
 * Implementation Project — API layer + React Query hooks
 *
 * Follows the Ops entity pattern (contract-year, ops-group):
 * inline Dataverse entity interfaces, mappers, API functions, hooks.
 */

import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { dataverseClient, TABLES, isMissingColumnError } from '@shared/api';
import { assertGuid, todayLocalIso } from '@shared/lib';
import type {
  ImpProject,
  ImpProjectSection,
  ImpProjectTask,
  ImpProjectDep,
  ImpProjectQueryParams,
  ImpProjectTaskQueryParams,
  UpdateImpProjectInput,
  UpdateImpProjectTaskInput,
} from '../model/types';
import { ProjectStatus, TaskStatus, TERMINAL_TASK_STATUSES, Criticality, TransitionType } from '../model/types';
import { localTodayStr, isTaskOverdue, computeProjectHealth, computeTaskCompletion } from '../model/health';
import { adminConfigApi, CONFIG_DEFAULTS, type HealthRulesValue } from '@shared/entities/admin-config';

// =============================================================================
// Dataverse Entity Interfaces (inline, per Ops convention)
// =============================================================================

interface ImpProjectEntity {
  vba_impprojectid: string;
  vba_name: string;
  _vba_template_value?: string;
  vba_templateversion?: string;
  _vba_group_value?: string;
  _vba_coordinator_value?: string;
  vba_startdate?: string;
  vba_effectivedate?: string;
  vba_impstatus?: number;
  vba_transitiontype?: number;
  vba_solutionscope?: string;
  _vba_contractyear_value?: string;
  vba_healthoverride?: number;
  vba_healthstatus?: number;
  vba_taskstotal?: number;
  vba_taskscomplete?: number;
  vba_tasksoverdue?: number;
  vba_pctcomplete?: number;
  vba_nextmilestonename?: string;
  vba_nextmilestonedate?: string;
  vba_sharepointfolderurl?: string;
  createdon?: string;
  modifiedon?: string;
  // Navigation properties (expanded)
  vba_impproject_sections?: ImpProjectSectionEntity[];
  [key: string]: unknown;
}

interface ImpProjectSectionEntity {
  vba_impprojectsectionid: string;
  vba_name: string;
  vba_impsortorder?: number;
  _vba_project_value?: string;
  _vba_sourcesection_value?: string;
  // Navigation
  vba_impprojectsection_tasks?: ImpProjectTaskEntity[];
  [key: string]: unknown;
}

interface ImpProjectTaskEntity {
  vba_impprojecttaskid: string;
  vba_name: string;
  vba_tasktype?: number;
  vba_impinstructions?: string;
  vba_impduration?: number;
  vba_impsortorder?: number;
  vba_impstatus?: number;
  // Dates
  vba_targetstart?: string;
  vba_imptargetend?: string;
  vba_datesent?: string;
  vba_datereceived?: string;
  vba_datecompleted?: string;
  vba_dateacknowledged?: string;
  // Assignment
  vba_assigneerole?: string;
  _vba_assignee_value?: string;
  _vba_contact_value?: string;
  vba_contactfreetext?: string;
  vba_imptags?: string;
  // Data binding
  vba_databindingentity?: string;
  vba_databindingfield?: string;
  vba_autocompleteondata?: boolean;
  vba_isgating?: boolean;
  vba_criticality?: number;
  vba_impact?: number;
  vba_includeinstatusreport?: boolean;
  vba_impoffsetdays?: number | null;
  vba_dateoverridden?: boolean | null;
  // Notes (free-form, per task)
  vba_impnotes?: string;
  // Document
  vba_sharepointsubfolder?: string;
  vba_sharepointurl?: string;
  vba_documentname?: string;
  // Hierarchy
  _vba_section_value?: string;
  _vba_project_value?: string;
  _vba_sourcetask_value?: string;
  _vba_parenttask_value?: string;
  // Audit
  _modifiedby_value?: string;
  modifiedon?: string;
  // Expanded parent project (My Tasks feed only — see listMyTasks): gives the
  // group label + id for deep-linking, project status for filtering, and the
  // transition context (type / go-live / health) the inbox rows surface.
  vba_Project?: {
    vba_name?: string;
    _vba_group_value?: string;
    vba_impstatus?: number;
    vba_transitiontype?: number;
    vba_effectivedate?: string;
    vba_healthstatus?: number;
    vba_healthoverride?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface ImpProjectDepEntity {
  vba_impprojectdepid: string;
  vba_name: string;
  vba_impdeptype?: number;
  vba_implagdays?: number;
  _vba_predecessor_value?: string;
  _vba_successor_value?: string;
  [key: string]: unknown;
}

// =============================================================================
// Mappers
// =============================================================================

/**
 * Solution scope is stored comma-separated. Empty (the normal case) means the
 * transition covers every solution the group has elected, so an absent value
 * and an empty list mean the same thing and both read as undefined.
 */
function parseSolutionScope(value: string | undefined): string[] | undefined {
  const codes = (value ?? '').split(',').map((c) => c.trim()).filter(Boolean);
  return codes.length > 0 ? codes : undefined;
}

function mapProjectToDomain(entity: ImpProjectEntity): ImpProject {
  return {
    id: entity.vba_impprojectid,
    name: entity.vba_name ?? '',
    templateId: entity._vba_template_value ?? undefined,
    templateVersion: entity.vba_templateversion ?? undefined,
    groupId: entity._vba_group_value ?? '',
    groupName: (entity['_vba_group_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    coordinatorId: entity._vba_coordinator_value ?? undefined,
    coordinatorName: (entity['_vba_coordinator_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    startDate: entity.vba_startdate ?? undefined,
    effectiveDate: entity.vba_effectivedate ?? undefined,
    status: entity.vba_impstatus ?? ProjectStatus.ACTIVE,
    transitionType: (entity.vba_transitiontype as ImpProject['transitionType']) ?? TransitionType.ONBOARDING,
    contractYearId: (entity['_vba_contractyear_value'] as string | undefined) ?? undefined,
    solutionScope: parseSolutionScope(entity.vba_solutionscope as string | undefined),
    healthOverride: entity.vba_healthoverride ?? undefined,
    healthStatus: entity.vba_healthstatus ?? undefined,
    tasksTotal: entity.vba_taskstotal ?? 0,
    tasksComplete: entity.vba_taskscomplete ?? 0,
    tasksOverdue: entity.vba_tasksoverdue ?? 0,
    pctComplete: entity.vba_pctcomplete ?? 0,
    nextMilestoneName: entity.vba_nextmilestonename ?? undefined,
    nextMilestoneDate: entity.vba_nextmilestonedate ?? undefined,
    sharePointFolderUrl: entity.vba_sharepointfolderurl ?? undefined,
    sections: entity.vba_impproject_sections?.map(mapSectionToDomain),
    rowVersion: entity['@odata.etag'] as string | undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
  };
}

function mapSectionToDomain(entity: ImpProjectSectionEntity): ImpProjectSection {
  return {
    id: entity.vba_impprojectsectionid,
    name: entity.vba_name ?? '',
    sortOrder: entity.vba_impsortorder ?? 0,
    projectId: entity._vba_project_value ?? '',
    sourceSectionId: entity._vba_sourcesection_value ?? undefined,
    tasks: entity.vba_impprojectsection_tasks?.map(mapTaskToDomain),
    rowVersion: entity['@odata.etag'] as string | undefined,
  };
}

function mapTaskToDomain(entity: ImpProjectTaskEntity): ImpProjectTask {
  return {
    id: entity.vba_impprojecttaskid,
    name: entity.vba_name ?? '',
    taskType: entity.vba_tasktype as ImpProjectTask['taskType'],
    instructions: entity.vba_impinstructions ?? undefined,
    durationDays: entity.vba_impduration ?? undefined,
    sortOrder: entity.vba_impsortorder ?? 0,
    status: entity.vba_impstatus ?? TaskStatus.NOT_STARTED,
    // Dates
    targetStart: entity.vba_targetstart ?? undefined,
    targetEnd: entity.vba_imptargetend ?? undefined,
    dateSent: entity.vba_datesent ?? undefined,
    dateReceived: entity.vba_datereceived ?? undefined,
    dateCompleted: entity.vba_datecompleted ?? undefined,
    dateAcknowledged: entity.vba_dateacknowledged ?? undefined,
    // Assignment
    assigneeRole: entity.vba_assigneerole ?? undefined,
    assigneeId: entity._vba_assignee_value ?? undefined,
    assigneeName: (entity['_vba_assignee_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    contactId: entity._vba_contact_value ?? undefined,
    contactFreeText: entity.vba_contactfreetext ?? undefined,
    tags: entity.vba_imptags ?? undefined,
    // Data binding
    dataBindingEntity: entity.vba_databindingentity ?? undefined,
    dataBindingField: entity.vba_databindingfield ?? undefined,
    autoCompleteOnData: entity.vba_autocompleteondata ?? undefined,
    isGating: entity.vba_isgating ?? undefined,
    criticality: (entity.vba_criticality as ImpProjectTask['criticality']) ?? undefined,
    impact: (entity.vba_impact as ImpProjectTask['impact']) ?? undefined,
    includeInStatusReport: entity.vba_includeinstatusreport ?? undefined,
    offsetDays: entity.vba_impoffsetdays ?? undefined,
    dateOverridden: entity.vba_dateoverridden ?? undefined,
    // Notes
    notes: entity.vba_impnotes ?? undefined,
    // Document
    sharePointSubfolder: entity.vba_sharepointsubfolder ?? undefined,
    sharePointUrl: entity.vba_sharepointurl ?? undefined,
    documentName: entity.vba_documentname ?? undefined,
    // Email template (C2) — stamped from the template task
    emailTemplateId: (entity as Record<string, unknown>).vba_emailtemplateid as string ?? undefined,
    meetingTemplateId: (entity as Record<string, unknown>).vba_meetingtemplateid as string ?? undefined,
    // Hierarchy
    sectionId: entity._vba_section_value ?? '',
    projectId: entity._vba_project_value ?? '',
    sourceTaskId: entity._vba_sourcetask_value ?? undefined,
    parentTaskId: entity._vba_parenttask_value ?? undefined,
    // Audit
    lastUpdatedBy: (entity['_modifiedby_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    lastUpdatedOn: entity.modifiedon ?? undefined,
    rowVersion: entity['@odata.etag'] as string | undefined,
    // Parent-project context (populated only when vba_Project is expanded, i.e.
    // the My Tasks feed). Group label prefers the group's formatted name, then
    // the project name; groupId is the OpsGroup record id used by /ops/groups/:id.
    groupId: (entity.vba_Project?._vba_group_value as string) ?? undefined,
    groupLabel:
      (entity.vba_Project?.['_vba_group_value@OData.Community.Display.V1.FormattedValue'] as string) ??
      (entity.vba_Project?.vba_name as string) ??
      undefined,
    projectStatus: (entity.vba_Project?.vba_impstatus as number) ?? undefined,
    transitionType: (entity.vba_Project?.vba_transitiontype as number) ?? undefined,
    projectEffectiveDate: (entity.vba_Project?.vba_effectivedate as string) ?? undefined,
    // Coordinator's manual override wins over the stored rollup (mirrors the panel).
    projectHealth:
      ((entity.vba_Project?.vba_healthoverride ?? entity.vba_Project?.vba_healthstatus) as number) ?? undefined,
  };
}

function mapDepToDomain(entity: ImpProjectDepEntity): ImpProjectDep {
  return {
    id: entity.vba_impprojectdepid,
    name: entity.vba_name ?? '',
    dependencyType: entity.vba_impdeptype ?? undefined,
    lagDays: entity.vba_implagdays ?? undefined,
    predecessorId: entity._vba_predecessor_value ?? '',
    successorId: entity._vba_successor_value ?? '',
    rowVersion: entity['@odata.etag'] as string | undefined,
  };
}

function mapProjectToEntity(input: UpdateImpProjectInput): Record<string, unknown> {
  const entity: Record<string, unknown> = {};

  if (input.name !== undefined) entity.vba_name = input.name;
  if (input.status !== undefined) entity.vba_impstatus = input.status;
  if (input.healthOverride !== undefined) entity.vba_healthoverride = input.healthOverride;
  if (input.startDate !== undefined) entity.vba_startdate = input.startDate;
  if (input.effectiveDate !== undefined) entity.vba_effectivedate = input.effectiveDate;
  if (input.sharePointFolderUrl !== undefined) entity.vba_sharepointfolderurl = input.sharePointFolderUrl;
  if (input.nextMilestoneName !== undefined) entity.vba_nextmilestonename = input.nextMilestoneName;
  if (input.nextMilestoneDate !== undefined) entity.vba_nextmilestonedate = input.nextMilestoneDate;

  // Lookups
  if (input.coordinatorId !== undefined) {
    entity['vba_Coordinator@odata.bind'] = input.coordinatorId
      ? `/systemusers(${assertGuid(input.coordinatorId, 'coordinatorId')})` : null;
  }

  return entity;
}

function mapTaskToEntity(input: UpdateImpProjectTaskInput): Record<string, unknown> {
  const entity: Record<string, unknown> = {};

  if (input.name !== undefined) entity.vba_name = input.name;
  if (input.status !== undefined) entity.vba_impstatus = input.status;
  if (input.instructions !== undefined) entity.vba_impinstructions = input.instructions;
  if (input.contactFreeText !== undefined) entity.vba_contactfreetext = input.contactFreeText;
  if (input.assigneeRole !== undefined) entity.vba_assigneerole = input.assigneeRole;
  if (input.sharePointUrl !== undefined) entity.vba_sharepointurl = input.sharePointUrl;
  if (input.notes !== undefined) entity.vba_impnotes = input.notes;
  if (input.dataBindingEntity !== undefined) entity.vba_databindingentity = input.dataBindingEntity;
  if (input.dataBindingField !== undefined) entity.vba_databindingfield = input.dataBindingField;

  // Date fields. Editing either target by hand marks the task hand-set, so a
  // later reschedule does not silently overwrite the coordinator's adjustment
  // (ADO #1661). An explicit dateOverridden in the input wins, which is how
  // "reset to calculated" hands the task back to the scheduler.
  if (input.targetStart !== undefined) entity.vba_targetstart = input.targetStart;
  if (input.targetEnd !== undefined) entity.vba_imptargetend = input.targetEnd;
  if (input.dateOverridden !== undefined) {
    entity.vba_dateoverridden = input.dateOverridden;
  } else if (input.targetStart !== undefined || input.targetEnd !== undefined) {
    entity.vba_dateoverridden = true;
  }
  if (input.dateSent !== undefined) entity.vba_datesent = input.dateSent;
  if (input.dateReceived !== undefined) entity.vba_datereceived = input.dateReceived;
  if (input.dateCompleted !== undefined) entity.vba_datecompleted = input.dateCompleted;
  if (input.dateAcknowledged !== undefined) entity.vba_dateacknowledged = input.dateAcknowledged;

  // Lookups
  if (input.assigneeId !== undefined) {
    entity['vba_Assignee@odata.bind'] = input.assigneeId
      ? `/systemusers(${assertGuid(input.assigneeId, 'assigneeId')})` : null;
  }
  if (input.contactId !== undefined) {
    entity['vba_Contact@odata.bind'] = input.contactId
      ? `/contacts(${assertGuid(input.contactId, 'contactId')})` : null;
  }

  return entity;
}

// =============================================================================
// API Functions
// =============================================================================

/** List projects with optional filters */
async function listProjects(params: ImpProjectQueryParams = {}): Promise<ImpProject[]> {
  const filters: string[] = [];
  if (params.groupId) filters.push(`_vba_group_value eq '${assertGuid(params.groupId, 'groupId')}'`);
  if (params.status != null) filters.push(`vba_impstatus eq ${params.status}`);
  if (params.coordinatorId) filters.push(`_vba_coordinator_value eq '${assertGuid(params.coordinatorId, 'coordinatorId')}'`);

  const orderBy = params.orderBy === 'effectiveDate' ? 'vba_effectivedate'
    : params.orderBy === 'startDate' ? 'vba_startdate'
    : 'modifiedon';

  const response = await dataverseClient.list<ImpProjectEntity>(TABLES.IMP_PROJECT, {
    filter: filters.length > 0 ? filters.join(' and ') : undefined,
    orderBy: `${orderBy} ${params.orderDirection ?? 'desc'}`,
  });

  return response.value.map(mapProjectToDomain);
}

/**
 * Get a single project with sections and tasks expanded.
 * Uses nested $expand: project → sections → tasks, sorted by sortOrder.
 */
async function getProject(id: string): Promise<ImpProject> {
  const entity = await dataverseClient.get<ImpProjectEntity>(TABLES.IMP_PROJECT, id, {
    expand: [
      {
        property: 'vba_impproject_sections',
        orderBy: 'vba_impsortorder asc',
        select: [
          'vba_impprojectsectionid', 'vba_name', 'vba_impsortorder',
          '_vba_project_value', '_vba_sourcesection_value',
        ],
      },
    ],
  });

  // Sections are expanded but tasks need a separate call per section
  // because Dataverse does not support nested $expand beyond one level.
  // We fetch all tasks for the project in a single list call and attach them.
  if (entity.vba_impproject_sections?.length) {
    const tasks = await listTasksByProject(id);
    const tasksBySection = new Map<string, ImpProjectTask[]>();
    for (const task of tasks) {
      const list = tasksBySection.get(task.sectionId) ?? [];
      list.push(task);
      tasksBySection.set(task.sectionId, list);
    }

    for (const section of entity.vba_impproject_sections) {
      section.vba_impprojectsection_tasks = undefined as unknown as ImpProjectTaskEntity[];
      // We'll attach domain tasks after mapping
    }

    const project = mapProjectToDomain(entity);
    if (project.sections) {
      for (const section of project.sections) {
        const flat = tasksBySection.get(section.id) ?? [];
        section.tasks = buildTaskTree(flat);
      }
    }
    return project;
  }

  return mapProjectToDomain(entity);
}

/**
 * Turn a flat task list (with parentTaskId back-refs) into a tree by
 * attaching each task to its parent's `children` array. Orphans (tasks
 * whose parent is in a different section, or deleted) are surfaced at
 * the root so nothing disappears.
 *
 * Children within each parent are sorted by sortOrder. Works for any
 * depth — the schema allows unlimited nesting.
 */
function buildTaskTree(flat: ImpProjectTask[]): ImpProjectTask[] {
  const byId = new Map<string, ImpProjectTask>();
  for (const t of flat) {
    // Reset children in case we're re-nesting an already-processed task
    byId.set(t.id, { ...t, children: [] });
  }
  const roots: ImpProjectTask[] = [];
  for (const original of flat) {
    const node = byId.get(original.id)!;
    const parent = original.parentTaskId ? byId.get(original.parentTaskId) : undefined;
    if (parent) {
      parent.children!.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortByOrder = (list: ImpProjectTask[]) => {
    list.sort((a, b) => a.sortOrder - b.sortOrder);
    for (const t of list) if (t.children && t.children.length > 0) sortByOrder(t.children);
  };
  sortByOrder(roots);
  return roots;
}

/** List projects for a specific group. Exported for non-hook callers (MEC onboarding, ADO #1744). */
export async function getProjectsByGroup(groupId: string): Promise<ImpProject[]> {
  return listProjects({ groupId, orderBy: 'effectiveDate', orderDirection: 'desc' });
}

/** Update project-level fields */
async function updateProject(id: string, input: UpdateImpProjectInput): Promise<void> {
  const entity = mapProjectToEntity(input);
  await dataverseClient.update(TABLES.IMP_PROJECT, id, entity);
}

/** List all tasks for a given project (flat list, sorted by section then task sort order) */
async function listTasksByProject(projectId: string): Promise<ImpProjectTask[]> {
  const response = await dataverseClient.list<ImpProjectTaskEntity>(TABLES.IMP_PROJECT_TASK, {
    filter: `_vba_project_value eq '${assertGuid(projectId, 'projectId')}'`,
    orderBy: 'vba_impsortorder asc',
  });
  return response.value.map(mapTaskToDomain);
}

/** Update a single task (status change, date entry, assignment, etc.) */
async function updateTask(id: string, input: UpdateImpProjectTaskInput): Promise<void> {
  const entity = mapTaskToEntity(input);
  await dataverseClient.update(TABLES.IMP_PROJECT_TASK, id, entity);
}

/**
 * Recompute the denormalized project rollups from the current task set and
 * write them back to the project record.
 *
 * Why this exists: the project's vba_taskscomplete / vba_pctcomplete /
 * vba_tasksoverdue / vba_healthstatus are written once at stamp time and are
 * NOT maintained by Dataverse. Without this recompute they stay frozen at
 * "0 / 0% / 0 / Green", which makes both the group panel header and the
 * cross-group Implementations dashboard show meaningless numbers. Call this
 * after any task status / target-date change.
 *
 * Counts LEAF tasks only (parent/container tasks derive their status from
 * their subtasks — see deriveParentStatus), matching the panel's section
 * stats and the workspace stat strip. Health is computed automatically; the
 * coordinator's manual healthOverride is stored separately and still wins at
 * read time.
 */
async function recalcProjectRollups(projectId: string): Promise<void> {
  const safeId = assertGuid(projectId, 'projectId');
  const [tasks, project] = await Promise.all([
    listTasksByProject(safeId),
    dataverseClient.get<ImpProjectEntity>(TABLES.IMP_PROJECT, safeId, {
      select: ['vba_impprojectid', 'vba_effectivedate'],
    }),
  ]);

  const todayStr = localTodayStr();
  // Count LEAF tasks only (7/23 review E11): a task with subtasks is a
  // container whose completion derives from its children, so counting it too
  // double-counts and skews the %. A leaf = no other task names it as parent.
  const parentIds = new Set(tasks.map((t) => t.parentTaskId).filter(Boolean));
  const leafTasks = tasks.filter((t) => !parentIds.has(t.id));
  // ADO #1243: waived work leaves the denominator. The three persisted fields
  // MUST tie out against each other, so vba_taskstotal stores the SCORED count
  // (Mark's "if there were 10 and one is Not Needed, the denominator is 9").
  // Storing 10 next to a 100% would render as "9 of 10, 100%" on every surface
  // that reads these rollups.
  const { scored: total, complete, pct } = computeTaskCompletion(leafTasks.map((t) => t.status));
  const overdueTasks = leafTasks.filter((t) => isTaskOverdue(t, todayStr));
  const overdue = overdueTasks.length;

  // Health (override stored separately, applied at read time). The rules are
  // Catalog-configurable (ADO #1725); an unreadable config falls back to the
  // defaults rather than leaving the rollup unwritten.
  const rulesConfig = await adminConfigApi
    .getByKey<HealthRulesValue>('implementation.healthRules')
    .catch(() => null);
  const health = computeProjectHealth({
    overdueTasks,
    rules: (rulesConfig?.value ?? CONFIG_DEFAULTS['implementation.healthRules'].value).rules,
    todayStr,
  });

  await dataverseClient.update(TABLES.IMP_PROJECT, safeId, {
    vba_taskstotal: total,
    vba_taskscomplete: complete,
    vba_tasksoverdue: overdue,
    vba_pctcomplete: pct,
    vba_healthstatus: health,
  });
}

/**
 * Cross-project task list: tasks assigned to a specific user.
 * Supports additional filters (status, overdue).
 */
async function listMyTasks(
  userId: string,
  params: ImpProjectTaskQueryParams = {},
): Promise<ImpProjectTask[]> {
  const filters: string[] = [
    `_vba_assignee_value eq '${assertGuid(userId, 'userId')}'`,
  ];

  if (params.status != null) {
    filters.push(`vba_impstatus eq ${params.status}`);
  }

  if (params.isOverdue) {
    // Overdue = targetEnd is in the past AND task is not in a terminal status
    const today = todayLocalIso();
    filters.push(`vba_imptargetend lt ${today}`);
    filters.push(`vba_impstatus ne ${TaskStatus.COMPLETE}`);
    filters.push(`vba_impstatus ne ${TaskStatus.NOT_NEEDED}`);
  }

  const orderBy = params.orderBy === 'targetEnd' ? 'vba_imptargetend'
    : params.orderBy === 'modifiedOn' ? 'modifiedon'
    : 'vba_impsortorder';

  const response = await dataverseClient.list<ImpProjectTaskEntity>(TABLES.IMP_PROJECT_TASK, {
    filter: filters.join(' and '),
    orderBy: `${orderBy} ${params.orderDirection ?? 'asc'}`,
    // Expand the parent project so each row carries its group label + id (for
    // the deep link), project status (to drop tasks on closed projects), and
    // the transition context the inbox surfaces — no N+1 per task.
    expand: [{
      property: 'vba_Project',
      select: [
        'vba_name', '_vba_group_value', 'vba_impstatus',
        'vba_transitiontype', 'vba_effectivedate', 'vba_healthstatus', 'vba_healthoverride',
      ],
    }],
  });

  return response.value.map(mapTaskToDomain);
}

/** Minimal task row for cross-project live rollup computation on the dashboard. */
export interface ImpTaskRollupRow {
  projectId: string;
  status: number;
  targetEnd?: string;
  criticality?: number;
  /** Task name — needed by the portfolio showstopper-aging panel (chunk 7). */
  name?: string;
  /** Actual completion date — feeds the portfolio slippage metric (chunk 7). */
  dateCompleted?: string;
  // The Showstoppers view (ADO #1748) lists open critical leaf tasks across
  // every transition, so the row also carries what a task line shows.
  /** Task id, and its parent's, so the view can keep leaf tasks only. */
  id?: string;
  parentTaskId?: string;
  /** Owning section's name (the lookup's formatted value). */
  sectionName?: string;
  /** Assigned user's name; the role when nobody is assigned yet. */
  assigneeName?: string;
  assigneeRole?: string;
  /** Internal / External / Both (ADO #1726). Undefined until provisioned. */
  impact?: number;
}

const ROLLUP_SELECT = [
  '_vba_project_value',
  'vba_impstatus',
  'vba_imptargetend',
  'vba_criticality',
  'vba_name',
  'vba_datecompleted',
  'vba_impprojecttaskid',
  '_vba_parenttask_value',
  '_vba_section_value',
  '_vba_assignee_value',
  'vba_assigneerole',
  'vba_impact',
];

/**
 * Fetch lightweight task rows (status / target-end / criticality / name /
 * completion) for a set of projects, so the dashboard can compute overdue +
 * health + slippage LIVE rather than trusting the stored rollups (which only
 * refresh when a task is edited). Name and completion date carry the
 * portfolio's showstopper-aging and slippage derivations (chunk 7); they add
 * two columns to a query the dashboard already runs across all active projects.
 */
async function listTaskRollupRowsForProjects(projectIds: string[]): Promise<ImpTaskRollupRow[]> {
  if (projectIds.length === 0) return [];
  const CHUNK_SIZE = 40;
  const rows: ImpTaskRollupRow[] = [];
  // vba_impact (ADO #1726) may not be provisioned on this org yet. The first
  // chunk that 400s on it drops it for the rest of the load, so the page reads
  // as before (no Impact pills) instead of failing.
  let select = ROLLUP_SELECT;
  for (let i = 0; i < projectIds.length; i += CHUNK_SIZE) {
    const chunk = projectIds.slice(i, i + CHUNK_SIZE);
    const filter = chunk.map((id) => `_vba_project_value eq '${id}'`).join(' or ');
    const listWith = (cols: string[]) =>
      dataverseClient.list<ImpProjectTaskEntity>(TABLES.IMP_PROJECT_TASK, { filter, select: cols });
    const res = await listWith(select).catch((err) => {
      if (!isMissingColumnError(err, 'vba_impact')) throw err;
      select = ROLLUP_SELECT.filter((c) => c !== 'vba_impact');
      return listWith(select);
    });
    for (const r of res.value) {
      rows.push({
        projectId: (r._vba_project_value as string) ?? '',
        status: r.vba_impstatus ?? TaskStatus.NOT_STARTED,
        targetEnd: r.vba_imptargetend ?? undefined,
        criticality: r.vba_criticality ?? undefined,
        name: r.vba_name ?? undefined,
        dateCompleted: r.vba_datecompleted ?? undefined,
        id: r.vba_impprojecttaskid,
        parentTaskId: r._vba_parenttask_value ?? undefined,
        sectionName: (r['_vba_section_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
        assigneeName: (r['_vba_assignee_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
        assigneeRole: r.vba_assigneerole ?? undefined,
        impact: r.vba_impact ?? undefined,
      });
    }
  }
  return rows;
}

/** List dependencies for a project */
async function listDependencies(projectId: string): Promise<ImpProjectDep[]> {
  // Deps have no direct project FK — they reference tasks via predecessor/successor.
  // Fetch this project's task IDs first, then filter deps where predecessor is in
  // that set. Filtering on predecessor alone is sufficient: project-internal deps
  // have both endpoints in the same project by construction.
  const safeProjectId = assertGuid(projectId, 'projectId');
  const tasksRes = await dataverseClient.list<{ vba_impprojecttaskid: string }>(
    TABLES.IMP_PROJECT_TASK,
    {
      filter: `_vba_project_value eq '${safeProjectId}'`,
      select: ['vba_impprojecttaskid'],
    },
  );
  const taskIds = tasksRes.value.map((t) => t.vba_impprojecttaskid).filter(Boolean);
  if (taskIds.length === 0) return [];

  // Chunk to keep filter URLs under common gateway limits (~16KB).
  // 50 IDs × ~65 chars per condition ≈ 3.3KB per chunk.
  const CHUNK_SIZE = 50;
  const deps: ImpProjectDepEntity[] = [];
  for (let i = 0; i < taskIds.length; i += CHUNK_SIZE) {
    const chunk = taskIds.slice(i, i + CHUNK_SIZE);
    const filter = chunk.map((id) => `_vba_predecessor_value eq '${id}'`).join(' or ');
    const res = await dataverseClient.list<ImpProjectDepEntity>(TABLES.IMP_PROJECT_DEP, { filter });
    deps.push(...res.value);
  }
  return deps.map(mapDepToDomain);
}

/** Create a dependency between two project tasks. */
async function createDependency(input: {
  name?: string;
  predecessorId: string;
  successorId: string;
  dependencyType?: number;
  lagDays?: number;
}): Promise<void> {
  await dataverseClient.create(TABLES.IMP_PROJECT_DEP, {
    vba_name: input.name ?? 'dep',
    vba_impdeptype: input.dependencyType ?? 100_000_000,
    vba_implagdays: input.lagDays ?? 0,
    'vba_Predecessor@odata.bind': `/${TABLES.IMP_PROJECT_TASK}(${assertGuid(input.predecessorId, 'predecessorId')})`,
    'vba_Successor@odata.bind': `/${TABLES.IMP_PROJECT_TASK}(${assertGuid(input.successorId, 'successorId')})`,
  });
}

/** Delete a project dependency. */
async function deleteDependency(id: string): Promise<void> {
  await dataverseClient.remove(TABLES.IMP_PROJECT_DEP, assertGuid(id, 'depId'));
}

// =============================================================================
// React Query Keys
// =============================================================================

const keys = {
  all: ['impProjects'] as const,
  lists: () => [...keys.all, 'list'] as const,
  list: (params: ImpProjectQueryParams) => [...keys.lists(), params] as const,
  detail: (id: string) => [...keys.all, 'detail', id] as const,
  tasks: () => ['impProjectTasks'] as const,
  tasksByProject: (projectId: string) => [...keys.tasks(), 'byProject', projectId] as const,
  myTasks: (userId: string, params?: ImpProjectTaskQueryParams) => [...keys.tasks(), 'my', userId, params] as const,
  deps: (projectId: string) => [...keys.all, 'deps', projectId] as const,
};

// =============================================================================
// React Query Hooks
// =============================================================================

/** All projects (optionally filtered) */
export function useImpProjects(
  params: ImpProjectQueryParams = {},
  options?: Omit<UseQueryOptions<ImpProject[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => listProjects(params),
    ...options,
  });
}

/** Single project with sections and tasks expanded */
export function useImpProject(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => getProject(id),
    enabled: !!id,
  });
}

/** Projects for a specific group */
export function useImpProjectsByGroup(groupId: string) {
  return useQuery({
    queryKey: keys.list({ groupId }),
    queryFn: () => getProjectsByGroup(groupId),
    enabled: !!groupId,
  });
}

/** Update project-level fields */
export function useUpdateImpProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateImpProjectInput }) => updateProject(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

/**
 * Update a single task. Pass `projectId` to recompute the project's
 * denormalized rollups (complete / overdue / pct / health) after the write
 * so the panel header and the cross-group dashboard stay accurate. Omit it
 * for changes that can't affect rollups (e.g. a notes-only edit).
 */
export function useUpdateImpProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input, projectId }: { id: string; input: UpdateImpProjectTaskInput; projectId?: string }) => {
      await updateTask(id, input);
      // Best-effort: a rollup hiccup must not make a saved task edit look failed.
      if (projectId) {
        try {
          await recalcProjectRollups(projectId);
        } catch (err) {
          console.error('Project rollup recompute failed (task change was saved):', err);
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.tasks() });
    },
  });
}

/**
 * Cross-project tasks for the current user (My Tasks Today).
 * Pass the authenticated user's systemuser GUID.
 */
export function useMyImplementationTasks(
  userId: string | undefined,
  params: ImpProjectTaskQueryParams = {},
) {
  return useQuery({
    queryKey: keys.myTasks(userId ?? '', params),
    queryFn: () => listMyTasks(userId!, params),
    enabled: !!userId,
  });
}

/** Live task rollup rows for a set of projects (dashboard live overdue/health). */
export function useImpTaskRollupRows(projectIds: string[]) {
  const sorted = [...projectIds].sort();
  return useQuery({
    queryKey: [...keys.tasks(), 'rollupRows', sorted],
    queryFn: () => listTaskRollupRowsForProjects(sorted),
    enabled: projectIds.length > 0,
  });
}

/** Dependencies for a project */
export function useImpProjectDeps(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.deps(projectId ?? ''),
    queryFn: () => listDependencies(projectId!),
    enabled: !!projectId,
  });
}

/** Create a project dependency. */
export function useCreateImpProjectDep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDependency,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

/** Delete a project dependency. */
export function useDeleteImpProjectDep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDependency(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

/** Stamp a template into a new project */
export function useStampProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: import('../model/types').StampProjectInput) => {
      const { stampProject } = await import('./stampProject');
      return stampProject(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
}

/**
 * Reschedule a project's target dates from (an optionally new) effective date,
 * then recompute rollups since overdue/health depend on the new dates.
 */
export function useRescheduleProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: import('./stampProject').RescheduleProjectInput) => {
      const { rescheduleProject } = await import('./stampProject');
      const result = await rescheduleProject(input);
      try {
        await recalcProjectRollups(input.projectId);
      } catch (err) {
        console.error('Project rollup recompute failed (reschedule succeeded):', err);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
      void queryClient.invalidateQueries({ queryKey: keys.tasks() });
    },
  });
}

/**
 * Preview what a reschedule would do without writing anything, so the
 * re-dating dialog can show the coordinator which hand-set dates are about to
 * move and by how much (ADO #1661). Read-only, so it is a mutation only in the
 * sense that the caller triggers it on demand.
 */
export function usePreviewReschedule() {
  return useMutation({
    mutationFn: async (input: import('./stampProject').RescheduleProjectInput) => {
      const { previewReschedule } = await import('./stampProject');
      return previewReschedule(input);
    },
  });
}

/**
 * Re-pull: append catalog library sections into an existing project, then
 * recompute the project rollups so the panel/dashboard reflect the new task
 * count immediately.
 */
export function useAddSectionsToProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: import('./stampProject').AddSectionsToProjectInput) => {
      const { stampSectionsIntoProject } = await import('./stampProject');
      const result = await stampSectionsIntoProject(input);
      // Best-effort: sections were added; a rollup hiccup shouldn't fail the op.
      try {
        await recalcProjectRollups(input.projectId);
      } catch (err) {
        console.error('Project rollup recompute failed (sections were added):', err);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
      void queryClient.invalidateQueries({ queryKey: keys.tasks() });
    },
  });
}
